import React from "react";
import { motion } from "framer-motion";
import { Compass, Sparkles, HeartHandshake, Trophy } from "lucide-react";
import DynamicImage from "@/components/common/DynamicImage";
import DirectorsSection from "@/components/about/DirectorsSection";
import SEO from "@/components/common/SEO";
import { fadeUp } from "@/lib/motion";
import { getPageSeo } from "@/lib/seo";

const CORE_VALUES = [
  {
    icon: HeartHandshake,
    title: "Integrity",
    desc:
      "Transparent land titles, ethical dealings, and a single source of truth across every contract we issue.",
  },
  {
    icon: Sparkles,
    title: "Innovation",
    desc:
      "Smart township planning, PropTech-enabled asset management, and sustainable design as default.",
  },
  {
    icon: Compass,
    title: "Customer-Centricity",
    desc:
      "Concierge-grade pre-sales advisory, lifelong relationship management, and white-glove service.",
  },
  {
    icon: Trophy,
    title: "Excellence",
    desc:
      "From road kerbs to clubhouse finishes — obsessive attention to detail at every touchpoint.",
  },
];

const AboutPage = () => {
  const seo = getPageSeo("about");
  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
      {/* HERO — half height */}
      <section data-testid="about-hero" className="relative h-[68vh] min-h-[520px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <DynamicImage
            mediaKey="hero_about"
            alt="Architects studying a master plan in a modern office"
            ratio=""
            className="h-full w-full"
            imgClassName="h-full w-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="container-narrow relative z-10 pb-16">
          <motion.p {...fadeUp} className="text-[11px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]">
            <span className="gold-divider" /> About Seetai Properties
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-4xl"
          >
            Visionaries in <span className="italic text-white/85">Modern Real Estate.</span>
          </motion.h1>
        </div>
      </section>

      {/* OUR STORY — typography led */}
      <section data-testid="about-story" className="luxe-section">
        <div className="container-narrow grid lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-4">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Our Story
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              A practice founded on patience.
            </h2>
            <div className="mt-8 space-y-5 text-white/70 text-sm">
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-white/55">Headquarters</p>
                <p className="mt-1 text-white">Pune • Pandharpur, Maharashtra</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-white/55">Focus</p>
                <p className="mt-1 text-white">Townships • Plotting • Commercial • PropTech</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-white/55">Discipline</p>
                <p className="mt-1 text-white">Institutional rigour • Generational thinking</p>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-8">
            <p className="drop-cap text-white/85 text-lg leading-[1.85]">
              Seetai Properties was born from a simple, uncompromising idea — that real
              estate, at its finest, is a form of architecture for life itself. We
              build townships and commercial estates not as transactions, but as
              long-horizon platforms for families, investors, and entrepreneurs to
              flourish on.
            </p>
            <p className="mt-6 text-white/75 text-lg leading-[1.85]">
              From land assembly and infrastructure design to clubhouse architecture
              and post-sale stewardship, every stage is treated as a craft —
              meticulously planned, slowly built, and quietly delivered. We are a
              practice of patient capital and uncommon precision.
            </p>
            <p className="mt-6 text-white/75 text-lg leading-[1.85]">
              Today, Seetai Properties is the trusted partner of choice for
              discerning patrons across Maharashtra and a growing community of
              global investors seeking quality, legality, and yield.
            </p>
          </motion.div>
        </div>
      </section>

      {/* VISION & MISSION — 50/50 split */}
      <section data-testid="about-vision-mission" className="luxe-section bg-[#0a0a0a] border-y border-white/5">
        <div className="container-narrow grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div {...fadeUp} className="relative lg:pr-10 lg:border-r border-white/10">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Vision
            </p>
            <h3 className="font-display text-3xl md:text-4xl mt-4 leading-[1.15]">
              To bridge the gap between imagination and reality by developing
              integrated, <span className="italic text-white/80">smart</span>, and <span className="italic text-[color:var(--sp-emerald)]">green</span> communities.
            </h3>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:pl-10">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Mission
            </p>
            <h3 className="font-display text-3xl md:text-4xl mt-4 leading-[1.15]">
              To build a better tomorrow with <span className="italic text-white/80">trust</span>, quality, and commitment — delivering
              generational wealth through strategic real estate.
            </h3>
          </motion.div>
        </div>
      </section>

      {/* CORE VALUES — 2x2 grid */}
      <section data-testid="about-values" className="luxe-section">
        <div className="container-narrow">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Core Values
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              The non-negotiables we live by.
            </h2>
          </motion.div>

          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            {CORE_VALUES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.08 * i }}
                className="glass-card neon-border-dual p-8 card-lift relative overflow-hidden"
              >
                <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[color:rgba(212,175,55,0.5)] to-transparent" />
                <div className="flex items-center justify-between">
                  <span className="font-display text-[10px] tracking-[0.32em] uppercase text-white/55">
                    0{i + 1}
                  </span>
                  <Icon className="h-5 w-5 text-[color:var(--sp-gold)]" />
                </div>
                <h3 className="font-display text-3xl mt-6">{title}</h3>
                <p className="mt-3 text-white/70 leading-relaxed text-[15px]">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP — DIRECTORS */}
      <DirectorsSection />
    </>
  );
};

export default AboutPage;
